/* eslint-disable no-useless-catch */
import React, { useState } from "react";
import { Button, Input, Image } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { BodyContainer, Container } from "@/styles/ContainerStyles";
import PhysicalButtonModal from "@/components/Modals/PhysicalButtonModal";
import PhysicalButtonCard from "@/components/Cards/PhysicalButtonCard/PhysicalButtonCard";
import {
  useAddPhysicalButton,
  useGetPhysicalButtons,
  useUnlinkPhysicalButton,
  useUpdatePhysicalButton,
} from "@/hooks/useUsers";
import { useParams } from "react-router-dom";
import Spin from "@/components/widgets/Spinner/Spin";

type userParams = {
  id?: string | undefined;
};

const ButtonManagment = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<"add" | "edit">("add");
  const [editingButton, setEditingButton] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { id } = useParams<userParams>();
  const addPhysicalButtonMutation = useAddPhysicalButton();
  const updatePhysicalButtonMutation = useUpdatePhysicalButton();
  const unlinkPhysicalButtonMutation = useUnlinkPhysicalButton();

  // Fetch user's physical buttons
  const {
    data: physicalButtonsResponse,
    isLoading: isLoadingButtons,
    isError,
    refetch: refetchButtons,
  } = useGetPhysicalButtons(Number(id));

  const physicalButtons = physicalButtonsResponse?.data || [];

  const handleAddButtonClick = () => {
    setModalAction("add");
    setEditingButton(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingButton(null);
  };

  const handleAddButton = async (values: IAddPhysicalButton): Promise<void> => {
    try {
      // Prepare the payload with all required fields
      const payload: IAddPhysicalButton = {
        ...values,
        userId: Number(id),
        ownerUserId: Number(id),
        assignedUserId: Number(id),
        preferredContactUserId: Number(id),
        physicalButtonTypeId:
          typeof values.physicalButtonTypeId === "string"
            ? parseInt(values.physicalButtonTypeId, 10)
            : values.physicalButtonTypeId,
        platform: "console",
      };

      await addPhysicalButtonMutation.mutateAsync(payload);

      // Refetch the physical buttons list to get updated data
      await refetchButtons();
    } catch (error: any) {
      // Error handling is done in the modal component
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  const handleEditButton = async (
    values: any // Using any here since we're mapping between different interfaces
  ): Promise<void> => {
    try {
      // Prepare the payload with all required fields for editing
      const payload: IUpdatePhysicalButton = {
        id: editingButton?.id || "",
        name: values.name,
        phoneNumber: values.phoneNumber, // Use phoneNumber field for edit mode
      };

      await updatePhysicalButtonMutation.mutateAsync(payload);

      await refetchButtons();
    } catch (error: any) {
      // Error handling is done in the modal component
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  const handleEditButtonClick = (button: any) => {
    setModalAction("edit");
    setEditingButton(button);
    setIsModalOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter buttons based on search term
  const filteredButtons = physicalButtons.filter(
    (button: any) =>
      button.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      button.deviceId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isError) {
    return (
      <Container className="w-full h-full">
        <BodyContainer className="w-full h-full">
          <div className="flex flex-col w-full h-full items-center justify-center">
            <p className="text-red-500 mb-4">Error loading physical buttons</p>
            <Button onClick={() => refetchButtons()}>Retry</Button>
          </div>
        </BodyContainer>
      </Container>
    );
  }

  return (
    <Spin tip="" spinning={isLoadingButtons} delay={500}>
      <Container className="w-full h-full">
        <BodyContainer className="w-full h-full">
          <div className="flex flex-row gap-2 w-1/3 items-center">
            <Input
              placeholder="Search"
              className="flex-1"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddButtonClick}
              disabled={
                addPhysicalButtonMutation.isPending ||
                unlinkPhysicalButtonMutation.isPending ||
                updatePhysicalButtonMutation.isPending
              }
            >
              Add Button
            </Button>
          </div>
          <div className="flex flex-col w-full h-full items-start pt-3">
            <p>Results: {filteredButtons.length} Buttons Linked</p>
            <div className="flex w-full h-full">
              {filteredButtons.length === 0 ? (
                <div className="flex flex-col w-full h-full items-center pt-10 justify-center">
                  <Image
                    src={require("../../assets/images/EmptyState.png")}
                    alt="No buttons linked"
                    height="200px"
                    width="200px"
                    preview={false}
                  />
                  <p>
                    {searchTerm
                      ? "No buttons match your search"
                      : "No buttons linked"}
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 w-full pt-4">
                  {/* Render the actual physical button cards */}
                  {filteredButtons.map((button: any, index: number) => (
                    <PhysicalButtonCard
                      userId={Number(id)}
                      key={button.id || index}
                      buttonId={button.id}
                      deviceName={button.name || `Device ${index + 1}`}
                      dateAdded={
                        button.dateAdded
                          ? new Date(button.dateAdded).toLocaleDateString()
                          : new Date().toLocaleDateString()
                      }
                      onEdit={() => handleEditButtonClick(button)}
                      refetchButtons={refetchButtons}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </BodyContainer>
      </Container>

      {/* Physical Button Modal - handles both add and edit */}
      <PhysicalButtonModal
        action={modalAction}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        handleAddButton={handleAddButton}
        handleEditButton={handleEditButton}
        isLoading={
          addPhysicalButtonMutation.isPending ||
          updatePhysicalButtonMutation.isPending
        }
        existingData={editingButton}
      />
    </Spin>
  );
};

export default ButtonManagment;
